// features/user/userMetaDataSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import axiosInstance from '../../utils/axiosInstance';

interface UserMetaData {
  showBranches?: boolean;
  [key: string]: any; // Allow dynamic fields
}

interface UserMetaDataState {
  userMetaData: UserMetaData | null;
  loading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: UserMetaDataState = {
  userMetaData: null,
  loading: false,
  error: null,
  success: false,
};

// Fetch user metadata
export const fetchUserMetaData = createAsyncThunk(
  'userMetaData/fetchUserMetaData',
  async (fields: string[], thunkAPI) => {
    try {
      const token = localStorage.getItem('viart_token');
      
      // Build query string if fields are specified
      const queryParams = fields && fields.length > 0 
        ? `?fields=${fields.join(',')}` 
        : '';
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/user-metadata${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.metadata;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || 'Failed to fetch user metadata'
      );
    }
  }
);
// Update user metadata
export const updateUserMetaData = createAsyncThunk(
  'userMetaData/updateUserMetaData',
  async (data: Record<string, any>, thunkAPI) => {
    try {
      const token = localStorage.getItem('viart_token');
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user-metadata/update`,
        data, // Send as JSON, not FormData
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data.metadata; // Returns updated metadata
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || 'Failed to update user metadata'
      );
    }
  }
);

const userMetaDataSlice = createSlice({
  name: 'userMetaData',
  initialState,
  reducers: {
    clearMetaDataError: (state) => {
      state.error = null;
    },
    clearMetaDataSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch user metadata
    builder
      .addCase(fetchUserMetaData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserMetaData.fulfilled, (state, action) => {
        state.loading = false;
        state.userMetaData = action.payload;
        state.error = null;
      })
      .addCase(fetchUserMetaData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update user metadata
    builder
      .addCase(updateUserMetaData.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateUserMetaData.fulfilled, (state, action) => {
        state.loading = false;
        state.userMetaData = action.payload;
        state.error = null;
        state.success = true;
      })
      .addCase(updateUserMetaData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });
  },
});

export const { clearMetaDataError, clearMetaDataSuccess } = userMetaDataSlice.actions;
export default userMetaDataSlice.reducer;